import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft, RefreshCw, Mail, CheckCircle2, AlertCircle, 
  ExternalLink, Send, Eye, MousePointerClick, XCircle,
  TrendingUp, BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailHistory() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const [emailsRes, statsRes] = await Promise.all([
        axios.get(`${API}/email/all?limit=100`),
        axios.get(`${API}/email/stats`)
      ]);
      setEmails(emailsRes.data.logs || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch email history");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success("Email history refreshed!");
  };

  const getStatusBadge = (email) => {
    if (email.status === 'bounced') {
      return (
        <Badge className="bg-[#fee2e2] text-[#dc2626] border-0">
          <XCircle className="w-3 h-3 mr-1" />
          Bounced
        </Badge>
      );
    }
    if (email.status === 'dropped') {
      return (
        <Badge className="bg-[#fee2e2] text-[#dc2626] border-0">
          <AlertCircle className="w-3 h-3 mr-1" />
          Dropped
        </Badge>
      );
    }
    if (email.status === 'delivered') {
      return (
        <Badge className="bg-[#dcfce7] text-[#166534] border-0">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Delivered
        </Badge>
      );
    }
    return (
      <Badge className="bg-brand-tint text-brand-dark border-0">
        <Send className="w-3 h-3 mr-1" />
        Sent
      </Badge>
    );
  };

  const getTrackingStatus = (email) => {
    const items = [];
    
    if (email.opened) {
      items.push(
        <span key="opened" className="inline-flex items-center gap-1 text-[#166534] bg-[#dcfce7] px-2 py-0.5 rounded-full text-xs">
          <Eye className="w-3 h-3" />
          Opened{email.open_count > 1 ? ` (${email.open_count}x)` : ''}
        </span>
      );
    } else {
      items.push(
        <span key="not-opened" className="inline-flex items-center gap-1 text-[#6b7280] bg-[#f3f4f6] px-2 py-0.5 rounded-full text-xs">
          <Mail className="w-3 h-3" />
          Not opened
        </span>
      );
    }
    
    if (email.clicked) {
      items.push(
        <span key="clicked" className="inline-flex items-center gap-1 text-[#7c3aed] bg-[#ede9fe] px-2 py-0.5 rounded-full text-xs ml-1">
          <MousePointerClick className="w-3 h-3" />
          Clicked{email.click_count > 1 ? ` (${email.click_count}x)` : ''}
        </span>
      );
    }
    
    return items;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-brand border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="text-[#6b7280]">Loading email history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-brand hover:text-brand-dark transition-colors" data-testid="back-to-home">
                <ArrowLeft className="w-4 h-4" />
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>
            <Button 
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="border-brand text-brand hover:bg-brand hover:text-white"
              data-testid="refresh-all-emails"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-brand-ink">Email History</h1>
          <p className="text-[#6b7280] mt-1">Track all sent emails and their engagement status</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <Card className="p-4 bg-white" data-testid="stat-total-sent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center">
                  <Send className="w-5 h-5 text-brand-dark" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_sent}</p>
                  <p className="text-xs text-[#6b7280]">Total Sent</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-white" data-testid="stat-delivered">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#dcfce7] flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#166534]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_delivered}</p>
                  <p className="text-xs text-[#6b7280]">Delivered</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-white" data-testid="stat-opened">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#fef3c7] flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#92400e]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_opened}</p>
                  <p className="text-xs text-[#6b7280]">Opened</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-white" data-testid="stat-clicked">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ede9fe] flex items-center justify-center">
                  <MousePointerClick className="w-5 h-5 text-[#7c3aed]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_clicked}</p>
                  <p className="text-xs text-[#6b7280]">Clicked</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-white" data-testid="stat-open-rate">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f0f9ff] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#0369a1]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.open_rate}%</p>
                  <p className="text-xs text-[#6b7280]">Open Rate</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-white" data-testid="stat-bounced">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#fee2e2] flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-[#dc2626]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_bounced}</p>
                  <p className="text-xs text-[#6b7280]">Bounced</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Email Table */}
        <Card className="bg-white overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <h2 className="font-semibold text-brand-ink flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              All Sent Emails
            </h2>
          </div>
          
          {emails.length === 0 ? (
            <div className="p-12 text-center">
              <Mail className="w-12 h-12 text-[#d1d5db] mx-auto mb-4" />
              <p className="text-[#6b7280] mb-2">No emails sent yet</p>
              <p className="text-sm text-[#9ca3af]">Emails you send will appear here with tracking information</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#f8fafc]">
                  <TableHead className="font-semibold text-[#374151]">Date & Time</TableHead>
                  <TableHead className="font-semibold text-[#374151]">Company</TableHead>
                  <TableHead className="font-semibold text-[#374151]">Recipient</TableHead>
                  <TableHead className="font-semibold text-[#374151]">Subject</TableHead>
                  <TableHead className="font-semibold text-[#374151]">Status</TableHead>
                  <TableHead className="font-semibold text-[#374151]">Tracking</TableHead>
                  <TableHead className="font-semibold text-[#374151]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map((email, index) => (
                  <TableRow key={email.id || index} className="hover:bg-[#f8fafc]" data-testid={`email-row-${index}`}>
                    <TableCell className="text-sm">
                      <div className="text-[#374151]">
                        {new Date(email.sent_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-[#6b7280]">
                        {new Date(email.sent_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-[#374151]">{email.company_name}</div>
                      {email.url && (
                        <a 
                          href={email.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-brand hover:underline flex items-center gap-1"
                        >
                          {new URL(email.url).hostname}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#374151]">
                      {email.to_email}
                    </TableCell>
                    <TableCell className="text-sm text-[#374151] max-w-[200px] truncate">
                      {email.subject}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(email)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {getTrackingStatus(email)}
                      </div>
                      {email.opened && email.opened_at && (
                        <div className="text-xs text-[#6b7280] mt-1">
                          {new Date(email.opened_at).toLocaleString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link to={`/results/${email.deliverable_id}`}>
                        <Button variant="ghost" size="sm" className="text-brand hover:text-brand-dark">
                          View Report
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>
    </div>
  );
}
